import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';
import { useTranslation } from '@hooks/useTranslation';

export const CLIENT_VIEW_BANNER_DISMISSED_KEY = 'clientViewBannerDismissed';

const readDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(CLIENT_VIEW_BANNER_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

interface ClientViewBannerProps {
  orgName: string;
}

/**
 * Sprint 43, story-223 (specified in story-128's UX, never built until now). One-line,
 * dismissible "you are viewing {org}'s projects as a client; view-only" notice. Dismissal is
 * session-scoped: it returns next session.
 */
const ClientViewBanner: React.FC<ClientViewBannerProps> = ({ orgName }) => {
  const tDashboard = useTranslation('dashboard');
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(CLIENT_VIEW_BANNER_DISMISSED_KEY, '1');
    } catch {
      // Storage unavailable: still hide it for this render lifetime.
    }
    setDismissed(true);
  };

  return (
    <div
      role="status"
      className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200"
    >
      <InfoOutlinedIcon sx={{ fontSize: 18 }} className="flex-shrink-0" />
      <span className="flex-1">
        {tDashboard.clientViewBanner.replace('{org}', orgName)}
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={tDashboard.clientViewBannerDismiss}
        className="flex-shrink-0 rounded p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  );
};

export default ClientViewBanner;
export { ClientViewBanner };
